import React, { useState, useEffect, useRef } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';
import { getFandoms, getFandomsSearch, getSearchFandoms } from '../../../util/api';
import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { propTypes } from '../../../util/types';
import {
  Avatar,
  InlineTextButton,
  Logo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';

import css from './TopbarDesktop.module.css';

import ShoppingCartTopLink from '../../ShoppingCartTopLink/ShoppingCartTopLink';
import DownloadAppModal from '../../DownloadAppModal/DownloadAppModal';

const TopbarDesktop = props => {
 
  const {
    className = null,
    appConfig = null,
    currentUser = null,
    currentPage   = null,
    rootClassName = null,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
  } = props;
  const [mounted, setMounted] = useState(false);
  const [downloadAppModal, setDownloadAppModal] = useState(false);
  const [fandoms, setFandoms] = useState([]);
  const [fandomsOpen, setFandomsOpen] = useState(false);
  const [fandomsSearch, setFandomsSearch] = useState('');
  const [filteredFandoms, setFilteredFandoms] = useState([]);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const fandomsButtonRef = React.useRef();
  const categoriesButtonRef = useRef();

  const handleModalDownloadApp = () => {
    setDownloadAppModal(prev => !prev);
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    
    getFandoms()
      .then(res => {
        console.log(res);
        const data = res.data[0].data || [];
        setFandoms(data.slice(0, 50));
      })
      .catch(() => setFandoms([]));
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (
        fandomsButtonRef.current &&
        !fandomsButtonRef.current.contains(e.target)
      ) {
        setFandomsOpen(false);
      }
    }
    if (fandomsOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [fandomsOpen]);

  // Close categories dropdown on outside click
  useEffect(() => {
    function handleClick(e) {
      if (
        categoriesButtonRef.current &&
        !categoriesButtonRef.current.contains(e.target)
      ) {
        setCategoriesOpen(false);
      }
    }
    if (categoriesOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [categoriesOpen]);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const showAuth0Login = !!process.env.REACT_APP_AUTH0_CLIENT_ID;

  const classes = classNames(rootClassName || css.root, className);

  const isVerified = currentUser?.attributes.profile.privateData?.isVerified;

  const search = (
    <TopbarSearchForm
      className={css.searchLink}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={appConfig}
    />
  );

  const notificationDot =
    notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const notificationDotSmall =
    notificationCount > 0 ? <div className={css.notificationDotSmall} /> : null;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' &&
      ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage
      ? css.currentPage
      : null;
  };
  const shopMenu = authenticatedOnClientSide ? (
    <NamedLink
      className={classNames(
        css.profileSettingsLink,
        currentPageClass('SellerDashBoardPage')
      )}
      name="SellerDashboardPage"
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.ManageShop" />
        {notificationDotSmall}
      </span>
    </NamedLink>
  ) : null;

  const profileMenu = authenticatedOnClientSide ? (
    <Menu>
      <MenuLabel
        className={css.profileMenuLabel}
        isOpenClassName={css.profileMenuIsOpen}
      >
        <div style={{ position: 'relative', height: 'contain', paddingBottom: '8px' }}>
          <Avatar
            className={css.avatar}
            user={currentUser}
            disableProfileLink
          />
          {notificationDot}
        </div>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(
              css.profileSettingsLink,
              currentPageClass('ProfileSettingsPage')
            )}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="InboxPageBuyer">
          <NamedLink
            className={classNames(
              css.yourListingsLink,
              currentPageClass('InboxPageBuyer')
            )}
            name="InboxPageBuyer"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.inboxPageBuyerLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="SellerDashboardPage">
          {shopMenu}
          <span className={css.menuItemBorder} />
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const getVerified =
    !isVerified || !authenticatedOnClientSide ? (
      <div
        onClick={() => handleModalDownloadApp()}
        className={css.inboxLink}
        name="SellerDashboardPage"
      >
        <span className={css.inbox}>Get Verified</span>
      </div>
    ) : null;
  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  const loginAndSignUpLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.loginOrSignUp" />
      </span>
    </NamedLink>
  );

  // Dropdown menu for categories (now using same structure as fandoms dropdown)
  const categories = [
    { label: 'Clothing', value: 'clothing' },
    { label: 'Accessories', value: 'accessories' },
    { label: 'Jewelry', value: 'jewelry' },
    { label: 'Bags', value: 'bags' },
    { label: 'Printables', value: 'printables' },
  ];

  const categoriesDropdown = (
    <div
      className={classNames(css.categoryLink, css.fandomsDropdownButton)}
      ref={categoriesButtonRef}
      tabIndex={0}
      onClick={e => {
        if (!categoriesOpen) setCategoriesOpen(true);
      }}
      aria-haspopup="true"
      aria-expanded={categoriesOpen}
      style={{ position: 'relative' }}
    >
      <span>Categories</span>
      {categoriesOpen && (
        <div
          className={css.fandomsDropdownGrid}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1000,
            background: 'white',
            border: '1px solid var(--color-border)',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: 200,
            maxWidth: 300,
            width: '60vw',
            display: 'flex',
            padding: 24,
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 200,
              maxHeight: 340,
              overflowY: 'scroll',
              display: 'flex',
              flexDirection: 'column',
              marginRight: '24px',
            }}
          >
            {categories.map(cat => (
              <NamedLink
                key={cat.value}
                className={css.categoryLink}
                name="SearchPage"
                to={{ search: `pub_category=${cat.value}` }}
                style={{ padding: 6, fontSize: 18 }}
                onClick={() => setCategoriesOpen(false)}
              >
                {cat.label}
              </NamedLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Split fandoms into promoted and rest
  const promotedFandoms = fandoms.slice(0, 3);
  const restFandoms = fandoms.slice(3);

  useEffect(() => {
    setFilteredFandoms(restFandoms);
  }, [fandoms]);

  useEffect(() => {
    if (fandomsSearch.trim() === '') {
      setFilteredFandoms(restFandoms);
    } else {
      getFandomsSearch(fandomsSearch.trim())
        .then(res => {
          console.log(res);
          // API returns array of { value, label }
          const data = res.data || [];
          setFilteredFandoms(data.map(f => ({ ...f, name: f.label, key: f.value })));
        })
        .catch(() => setFilteredFandoms([]));
    }
  }, [fandomsSearch]);

  const fandomsDropdown = (
    <div
      className={classNames(css.categoryLink, css.fandomsDropdownButton)}
      ref={fandomsButtonRef}
      tabIndex={0}
      onClick={e => {
        if (!fandomsOpen) setFandomsOpen(true);
      }}
      aria-haspopup="true"
      aria-expanded={fandomsOpen}
      style={{ position: 'relative' }}
    >
      <span>Fandoms</span>
      {fandomsOpen && (
        <div
          className={css.fandomsDropdownGrid}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1000,
            background: 'white',
            border: '1px solid var(--color-border)',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            minWidth: 500,
            maxWidth: 900,
            width: '60vw',
            display: 'flex',
            padding: 24,
          }}
        >
          <div
            className={css.fandomsListColumn}
            style={{
              flex: 1,
              minWidth: 200,
              maxHeight: 340,
              overflowY: 'scroll',
              display: 'flex',
              flexDirection: 'column',
              marginRight: '24px',
            }}
          >
            <h5 style={{ color: '#101010', marginBottom: '8px' }}>Top Fandoms</h5>

            {filteredFandoms.slice(0, 5).map((f, idx) => (
              <NamedLink
                key={f.value || f._key || idx}
                className={css.fandomLink}
                name="SearchPage"
                to={{ search: `pub_fandom=${encodeURIComponent(f._key || f.value)}` }}
              >
                {f.name}
              </NamedLink>
            ))}
            <NamedLink
              className={css.seeAllButton}
              name="FandomsList"
              style={{ marginTop: '16px', textAlign: 'left', fontWeight: 'bold', color: 'var(--color-primary)' }}
            >
              See all Fandoms
            </NamedLink>
          </div>
          <div style={{ overflow: 'hidden' }}>
            <h5 style={{ color: '#101010' }}>Featured Fandoms</h5>
            <div
              className={css.fandomsTilesColumn}
              style={{
                flex: 2,
                minWidth: 300,
                maxWidth: 600,
                display: 'flex',
                flexDirection: 'row',
                gap: 0,
                overflowX: 'auto',
                alignItems: 'flex-start',
                paddingBottom: 8,
              }}
            >
              {promotedFandoms.map((f, idx) => (
                <NamedLink
                  key={f.key || f.id || idx}
                  className={css.fandomTile}
                  name="SearchPage"
                  to={{ search: `pub_fandom=${encodeURIComponent(f._key)}` }}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'left',
                    fontWeight: 'bold',
                    paddingRight: 16,
                    paddingTop: 16,
                    fontSize: 20,
                    minWidth: 220,
                    maxWidth: 260,
                    minHeight: 220,
                    boxSizing: 'border-box',
                  }}
                >
                  {f.cover_image_url && (
                    <img
                      src={f.cover_image_url}
                      alt={f.name}
                      style={{
                        width: 208,
                        height: 258,
                        objectFit: 'cover',
                        borderRadius: 12,
                        background: '#eee',
                        marginBottom: 12,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                      }}
                    />
                  )}
                  <span
                    style={{
                      textAlign: 'left',
                      width: '100%',
                      fontWeight: 600,
                      fontSize: 22,
                      color: 'black',
                    }}
                  >
                    {f.name}
                  </span>
                </NamedLink>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <nav className={classes}>
      <NamedLink className={css.logoLink} name="LandingPage">
        <Logo
          format="desktop"
          className={css.logo}
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
        />
      </NamedLink>
      {categoriesDropdown}
      {fandomsDropdown}
      {search}
      <div className={css.separator}></div>
      <div className={css.separator}></div>
      <div className={css.separator}></div>
      <NamedLink name="ShoppingCart" className={css.ShoppingCartLink}>
        <ShoppingCartTopLink />
      </NamedLink>
      {profileMenu}
      {showAuth0Login ? loginAndSignUpLink : null}
      <DownloadAppModal
        isOpen={downloadAppModal}
        onClose={() => handleModalDownloadApp()}
      />
    </nav>
  );
};



TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
};

export default TopbarDesktop;
